import { db } from './db';
import { TABLES, type NomenclatureTable } from './tables';
import { encryptFields, resetKeyCache } from './crypto';
import { fetchSyncHashes, fetchTableRows } from './api';
import { invalidateAppointmentCache } from './appointmentRepo';
import { invalidateResolverCache } from './resolver';

/**
 * Nomenclature sync engine — the web port of the mobile `AzureSyncService`.
 *
 * Pulls `GET /mobile/azure/sync-hashes`, compares each table's hash to the one
 * we cached, and for every changed table fetches its rows and atomically
 * REPLACES the whole IndexedDB store (clear + bulkAdd in one transaction).
 *
 * Full-table replace means we don't need the mobile app's per-row `batch_hash`
 * bookkeeping — the per-table hash alone tells us when to refresh.
 *
 * Guards: single in-flight run + a minimum interval between runs.
 */

const MIN_INTERVAL_MS = 120_000; // 2 min, matching mobile
const HASH_PREFIX = 'hash:';

export interface SyncResult {
  skipped: boolean;
  synced: string[];
  upToDate: string[];
  error?: string;
}

let inFlight: Promise<SyncResult> | null = null;
let lastRunAt = 0;

export function syncNomenclatureIfStale(force = false): Promise<SyncResult> {
  if (inFlight) return inFlight;
  if (!force && Date.now() - lastRunAt < MIN_INTERVAL_MS) {
    return Promise.resolve({ skipped: true, synced: [], upToDate: [] });
  }
  inFlight = run().finally(() => {
    inFlight = null;
    lastRunAt = Date.now();
  });
  return inFlight;
}

async function getLocalHash(store: string): Promise<string | undefined> {
  const row = await db.meta.get(HASH_PREFIX + store);
  return typeof row?.value === 'string' ? row.value : undefined;
}

async function syncTable(table: NomenclatureTable, serverHash: string): Promise<void> {
  const rows = await fetchTableRows(table.endpoint);

  const records = await Promise.all(
    rows.map(async (row) => {
      if (table.encrypt.length === 0) return { ...row };
      const enc = await encryptFields(row, table.encrypt);
      const rec: Record<string, unknown> = { ...row };
      for (const f of table.encrypt) delete rec[f];
      if (enc) rec._enc = enc;
      return rec;
    }),
  );

  await db.transaction('rw', db.rows(table.store), db.meta, async () => {
    await db.rows(table.store).clear();
    await db.rows(table.store).bulkAdd(records as never);
    await db.meta.put({ key: HASH_PREFIX + table.store, value: serverHash });
  });
}

async function run(): Promise<SyncResult> {
  try {
    const serverHashes = await fetchSyncHashes();
    const synced: string[] = [];
    const upToDate: string[] = [];

    for (const table of TABLES) {
      const serverHash = serverHashes[table.hashKey];
      if (!serverHash) continue;
      const localHash = await getLocalHash(table.store);
      if (localHash === serverHash) {
        upToDate.push(table.store);
        continue;
      }
      await syncTable(table, serverHash);
      synced.push(table.store);
    }

    // Any replaced store invalidates the in-memory projections.
    if (synced.length > 0) {
      invalidateAppointmentCache();
      invalidateResolverCache();
    }

    await db.meta.put({ key: 'lastSyncAt', value: Date.now() });
    return { skipped: false, synced, upToDate };
  } catch (e) {
    return {
      skipped: false,
      synced: [],
      upToDate: [],
      error: e instanceof Error ? e.message : String(e),
    };
  }
}

/** Wipe the entire nomenclature cache + crypto key (e.g. on logout). */
export async function clearNomenclature(): Promise<void> {
  lastRunAt = 0;
  invalidateAppointmentCache();
  invalidateResolverCache();
  await db.delete();
  await db.open();
  // Drop the cached crypto key too, so the next sync generates + persists a
  // fresh key consistent with the data it writes (survives a page reload).
  resetKeyCache();
}
